import cx from 'clsx';
import React from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as InfoIcon } from '@assets/svg/ic-info.svg';

import Checkbox from '@components/uikit/Checkbox/Checkbox';
import RadioButton from '@components/uikit/RadioButton/RadioButton';

import useTranslation from '@hooks/useTranslation';

import type { IInstallationPoint } from '@model/installationPoint';

import type { errorMessages } from '../../EditSensorGalleryModal';
import { IMAGE_TYPES, IMAGE_TYPES_TOOLTIPS } from '../../constants/imageTypes';
import type { GalleryPhotoType, IGalleryPhoto } from '../../constants/sensorGalleryType';
import style from './style.module.scss';

interface IImageState {
  images: IGalleryPhoto[];
  uploadedImages: IGalleryPhoto[];
}

type ImageType = 'images' | 'uploadedImages';

interface IProps {
  currentSlide: IGalleryPhoto;
  installationPoints: IInstallationPoint[];
  imageType: ImageType;
  indexPhoto: number;
  equipmentName: string;
  isMainPhotoExist: boolean;
  setImageState: React.Dispatch<React.SetStateAction<IImageState>>;
  setErrorMessage: React.Dispatch<React.SetStateAction<keyof typeof errorMessages>>;
}

const ControlPanel = ({
  currentSlide,
  installationPoints,
  indexPhoto,
  imageType,
  equipmentName,
  isMainPhotoExist,
  setImageState,
  setErrorMessage,
}: IProps) => {
  const { t } = useTranslation();

  const isAllChecked =
    installationPoints &&
    installationPoints.length > 0 &&
    installationPoints.length === currentSlide.installationPoint?.length;

  const handleTypeChange = (newType: GalleryPhotoType) => {
    setImageState((prevState) => {
      const newArray = [...prevState[imageType]];
      const currentImage = newArray[indexPhoto];

      let updatedImage = {
        ...currentImage,
        imageType: newType,
      };

      if (newType === 'nameplatePhoto') {
        updatedImage = {
          ...updatedImage,
          installationPoint: [],
        };
      } else if (newType === 'installationPointImage') {
        const firstRadioValue = {
          id: installationPoints[0]?.id,
          name: installationPoints[0]?.name,
        };
        updatedImage = {
          ...updatedImage,
          installationPoint: firstRadioValue.id ? [firstRadioValue] : [],
        };
      }

      newArray[indexPhoto] = updatedImage;

      return {
        ...prevState,
        [imageType]: newArray,
      };
    });
    setErrorMessage('noErrorMessage');
  };

  const handleRadioInstallationPointChange = (point: { id: number; name: string }) => {
    setImageState((prevState) => {
      const newArray = [...prevState[imageType]];
      const currentSlide = newArray[indexPhoto];

      newArray[indexPhoto] = {
        ...currentSlide,
        installationPoint: [{ id: point.id, name: point.name }],
      };

      return {
        ...prevState,
        [imageType]: newArray,
      };
    });
  };

  const handleCheckboxChange = (point: { id: number; name: string }, checked: boolean) => {
    setImageState((prevState) => {
      const newArray = [...prevState[imageType]];
      const currentSlide = newArray[indexPhoto];
      const installationPoint = currentSlide.installationPoint ? [...currentSlide.installationPoint] : [];

      if (checked) {
        if (!installationPoint.some((p) => p.id === point.id)) {
          installationPoint.push(point);
        }
      } else {
        const idx = installationPoint.findIndex((p) => p.id === point.id);
        if (idx !== -1) {
          installationPoint.splice(idx, 1);
        }
      }

      newArray[indexPhoto] = {
        ...currentSlide,
        installationPoint,
      };

      return {
        ...prevState,
        [imageType]: newArray,
      };
    });
  };

  const handleEquipmentCheckboxChange = (checked: boolean) => {
    setImageState((prevState) => {
      const newArray = [...prevState[imageType]];
      const currentSlide = newArray[indexPhoto];

      let installationPoint: any;

      if (checked) {
        installationPoint = [...installationPoints];
      } else {
        installationPoint = [];
      }

      newArray[indexPhoto] = {
        ...currentSlide,
        installationPoint,
        equipmentId: checked && installationPoints[0] ? String(installationPoints[0].equipmentId) : undefined,
      };

      return {
        ...prevState,
        [imageType]: newArray,
      };
    });
  };

  const renderInstaPointsList = (photoType: string) => {
    switch (photoType) {
      case 'nameplatePhoto':
        return (
          <div className={style.checkboxContainer}>
            {installationPoints.map((point) => {
              const isChecked =
                currentSlide.installationPoint?.some(
                  (equipment) => equipment.id === point.id || equipment.equipment_id === point.id,
                ) || false;

              return (
                <Checkbox
                  key={point.id}
                  value={isChecked}
                  onChange={() => handleCheckboxChange(point, !isChecked)}
                  label={point.name}
                  withHelperText={false}
                />
              );
            })}
          </div>
        );

      case 'installationPointImage':
        return (
          <div className={style.radioButtonContainer}>
            {installationPoints.map((point) => {
              const isChecked =
                currentSlide.installationPoint?.some((equipment) => equipment.id === point.id) || false;

              return (
                <RadioButton
                  key={point.id}
                  id={`${point.id}`}
                  label={point.name}
                  checked={isChecked}
                  onChange={() => handleRadioInstallationPointChange(point)}
                />
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={style.controlPanel}>
      <div className={style.controlLabel}>
        <span>{t('Photo Type')}</span>
        <InfoIcon
          data-tooltip-id="info-icon"
          data-tooltip-place="left-start"
          data-tooltip-content={t('Select a photo type to define how this image will be displayed and used')}
          className={style.infoIcon}
        />
      </div>
      <div className={style.imageTypeRadioGroup}>
        {Object.entries(IMAGE_TYPES).map((type, index) => {
          const selectedImageType = currentSlide?.imageType === type[0];
          return (
            <div
              key={type[0]}
              data-tooltip-id={'info-icon'}
              data-tooltip-place="bottom"
              data-tooltip-content={t(IMAGE_TYPES_TOOLTIPS[type[1]])}
            >
              <RadioButton
                checked={selectedImageType}
                disabled={index === 0 && isMainPhotoExist && !selectedImageType}
                id={type[0]}
                customCls={cx({
                  [style.disabledRadio]: index === 0 && isMainPhotoExist && !selectedImageType,
                })}
                label={type[1] as string}
                onChange={() => handleTypeChange(type[0] as GalleryPhotoType)}
              />
            </div>
          );
        })}
      </div>
      {(currentSlide.imageType === 'nameplatePhoto' ||
        currentSlide.imageType === 'installationPointImage') && (
        <>
          <div className={style.controlLabel}>
            <span>{t('Installation Point Name')}</span>
            <InfoIcon
              data-tooltip-id="info-icon"
              data-tooltip-place="left-start"
              data-tooltip-content={t(
                'Select installation points to associate this photo with a specific area.',
              )}
              className={style.infoIcon}
            />
          </div>
          <div className={style.pointsListContainer}>
            {currentSlide.imageType === 'nameplatePhoto' && (
              <div className={style.equipmentCheckbox}>
                <Checkbox
                  label={equipmentName}
                  value={isAllChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleEquipmentCheckboxChange(e.target.checked)
                  }
                  className={style.allCheckbox}
                  labelClassName={style.allCheckboxLabel}
                  withHelperText={false}
                />
              </div>
            )}
            <div
              className={cx(style.pointList, {
                [style.withEquipmentCheckbox]: currentSlide.imageType === 'nameplatePhoto',
              })}
            >
              {renderInstaPointsList(currentSlide.imageType)}
            </div>
          </div>
        </>
      )}
      <Tooltip id="info-icon" className={style.controlPanelTooltip} />
    </div>
  );
};

export default ControlPanel;
